import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { api, CATEGORIES, SUBCATEGORIES } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PlaceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", category: "food", subcategory: "", description: "", address: "", city: "Saint-Denis",
    phone: "", website: "", image_url: "", hours: "", lat: -21.1151, lng: 55.5364, event_date: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/places/${id}`).then(r => setForm(r.data));
    }
  }, [id, isEdit]);

  if (authLoading) return <div className="pt-32 text-center">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
      if (payload.event_date) {
        try { payload.event_date = new Date(payload.event_date).toISOString(); } catch {}
      } else {
        payload.event_date = null;
      }
      if (isEdit) {
        await api.put(`/places/${id}`, payload);
        toast.success("Mise à jour effectuée");
      } else {
        await api.post(`/places`, payload);
        toast.success("Établissement créé !");
      }
      nav("/merchant");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F0EA]" data-testid="place-form-page">
      <div className="pt-24 max-w-3xl mx-auto px-6 pb-16">
        <Link to="/merchant" className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C6B55] hover:text-[#E26D5C] mb-6" data-testid="form-back">
          <ArrowLeft size={16}/> Retour au tableau de bord
        </Link>
        <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-8">
          <h1 className="font-display font-black text-3xl tracking-tighter">
            {isEdit ? "Modifier" : "Nouvel"} établissement
          </h1>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="place-form">
            <Field label="Nom" className="sm:col-span-2">
              <Input data-testid="form-name" required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Catégorie">
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v, subcategory: ""})}>
                <SelectTrigger data-testid="form-category" className="rounded-xl"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sous-catégorie">
              <Select value={form.subcategory || "none"} onValueChange={(v)=>setForm({...form, subcategory: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="form-subcategory" className="rounded-xl"><SelectValue placeholder="Choisir…"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {(SUBCATEGORIES[form.category] || []).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Ville">
              <Input data-testid="form-city" required value={form.city} onChange={e=>setForm({...form, city: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Adresse" className="sm:col-span-2">
              <Input data-testid="form-address" required value={form.address} onChange={e=>setForm({...form, address: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Latitude">
              <Input data-testid="form-lat" type="number" step="0.0001" required value={form.lat} onChange={e=>setForm({...form, lat: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Longitude">
              <Input data-testid="form-lng" type="number" step="0.0001" required value={form.lng} onChange={e=>setForm({...form, lng: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Téléphone">
              <Input data-testid="form-phone" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Horaires">
              <Input data-testid="form-hours" value={form.hours} onChange={e=>setForm({...form, hours: e.target.value})} className="rounded-xl" placeholder="Lun-Sam 9h-19h"/>
            </Field>
            <Field label="URL image" className="sm:col-span-2">
              <Input data-testid="form-image" value={form.image_url} onChange={e=>setForm({...form, image_url: e.target.value})} className="rounded-xl" placeholder="https://…"/>
            </Field>
            <Field label="Site web" className="sm:col-span-2">
              <Input data-testid="form-website" value={form.website} onChange={e=>setForm({...form, website: e.target.value})} className="rounded-xl"/>
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <Textarea data-testid="form-description" rows={4} value={form.description} onChange={e=>setForm({...form, description: e.target.value})} className="rounded-xl"/>
            </Field>
            {form.category === "events" && (
              <Field label="Date de l'événement" className="sm:col-span-2">
                <Input data-testid="form-event-date" type="datetime-local" value={form.event_date || ""} onChange={e=>setForm({...form, event_date: e.target.value})} className="rounded-xl"/>
              </Field>
            )}
            <div className="sm:col-span-2 flex gap-3">
              <Button type="submit" data-testid="form-submit" disabled={loading} className="rounded-full bg-[#E26D5C] hover:bg-[#D15B4A] text-white py-6 px-8">
                {loading ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-[#5C6B55]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
